import {
  Box,
  CircularProgress,
  Container,
  Grid,
  Select,
  MenuItem,
} from "@mui/material";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { SEO } from "../../components/common/SEO";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { Product } from "../../redux/types/productTypes";
import { RootState } from "../../redux/store";
import {
  fetchProducts,
  HARDCODED_CATEGORIES,
  SORT_BY,
} from "../../redux/actions/productActions";
import { useNavigate, useParams } from "react-router-dom";
import ToyCard from "../../components/product/ToyCard";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import { TotalResults } from "../../components/product/TotalResults";
import { getOrigin } from "../../utils";
import { MenuList } from "../../components/common/MenuList";

const PAGE_SIZE = 18;

const ProductByCategory: React.FC = () => {
  const { category: categoryParam, subCategory: subCategoryParam } =
    useParams();
  const category = categoryParam?.toLowerCase();
  const subCategory = subCategoryParam?.toLowerCase();
  const navigate = useNavigate();

  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<SORT_BY>(SORT_BY.LATEST);
  const [isScrolled, setIScrolled] = useState<boolean>(false);
  const [isReloadFromFilter, setIsReloadFromFilter] = useState<boolean>(false);
  const [savedCategory, setSavedCategory] = useState<string>();
  const [savedSubCategory, setSavedSubCategory] = useState<string>();
  const [savedCategorySlug, setSavedCategorySlug] = useState<string>();
  const [savedSubCategorySlug, setSavedSubCategorySlug] = useState<string>();
  const [savedSubCategoryDescription, setSavedSubCategoryDescription] =
    useState<string>();

  const dispatch = useDispatch();
  const { loading, productsData, error } = useSelector(
    (state: RootState) => state.products
  );
  const { products, totalRecords } = productsData;
  const isCategoryChangeRef = useRef(false);
  const previousCategoryRef = useRef<string>("");
  const previousSubCategoryRef = useRef<string>("");
  const isFetchingRef = useRef(false);
  const lastFetchedDocIdRef = useRef<string | null>(null);
  const hasMoreProductsRef = useRef(true);
  const previousProductsLengthRef = useRef<number>(0);

  useEffect(() => {
    // Only update when loading is false and we have products
    if (!loading && products.length > 0) {
      if (category && subCategory) {
        setSavedSubCategory(products[0].productSubCategoryName);
        setSavedSubCategorySlug(products[0].productSubCategorySlug);
        setSavedSubCategoryDescription(
          products[0].productSubCategory.description
        );
      }
      setSavedCategory(products[0].productCategoryName);
      if (category && !Object.keys(HARDCODED_CATEGORIES).includes(category)) {
        setSavedCategorySlug(products[0].productCategorySlug);
      } else {
        setSavedCategorySlug(category);
      }

      if (isCategoryChangeRef.current || isReloadFromFilter) {
        // Replace products when category/filter changes - batch update to prevent blinking
        const timeoutId = setTimeout(() => {
          setAllProducts(products);
          isCategoryChangeRef.current = false;
          setIsReloadFromFilter(false);
          isFetchingRef.current = false;
          // Reset to null so pagination can work - we'll set it when we start pagination
          lastFetchedDocIdRef.current = null;
          previousProductsLengthRef.current = products.length;
          hasMoreProductsRef.current =
            products.length < (totalRecords || Infinity);
        }, 0);
        return () => clearTimeout(timeoutId);
      } else {
        // Append products for pagination
        setAllProducts((prev) => {
          // Prevent duplicates by checking documentId
          const existingIds = new Set(prev.map((p) => p.documentId));
          const newProducts = products.filter(
            (p) => !existingIds.has(p.documentId)
          );
          if (newProducts.length === 0) {
            // No new products - we've reached the end
            hasMoreProductsRef.current = false;
            isFetchingRef.current = false;
            return prev;
          }
          const updatedProducts = [...prev, ...newProducts];
          // Check if we've loaded all products
          hasMoreProductsRef.current =
            updatedProducts.length < (totalRecords || Infinity);
          isFetchingRef.current = false;
          previousProductsLengthRef.current = prev.length; // Track where new products start
          // Reset lastFetchedDocIdRef so we can fetch the next page
          // We only keep it set while fetching to prevent duplicate fetches
          if (newProducts.length > 0 && hasMoreProductsRef.current) {
            lastFetchedDocIdRef.current = null; // Reset so next scroll can fetch
          } else {
            // No more products, keep it set to prevent further fetches
            lastFetchedDocIdRef.current =
              updatedProducts[updatedProducts.length - 1]?.documentId || null;
          }
          return updatedProducts;
        });
      }
    } else if (!loading && products.length === 0) {
      // No more products to load
      hasMoreProductsRef.current = false;
      isFetchingRef.current = false;
      if (isCategoryChangeRef.current || isReloadFromFilter) {
        isCategoryChangeRef.current = false;
        setIsReloadFromFilter(false);
        setAllProducts([]);
      }
    }
    // Note: isReloadFromFilter intentionally excluded from deps - including it causes the effect
    // to re-run when it's set to false, triggering the pagination branch incorrectly
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, loading, category, subCategory, totalRecords]);

  useEffect(() => {
    const isCategoryChange =
      previousCategoryRef.current !== category ||
      previousSubCategoryRef.current !== subCategory;
    previousCategoryRef.current = category || "";
    previousSubCategoryRef.current = subCategory || "";

    if (isCategoryChange && !isFetchingRef.current) {
      isCategoryChangeRef.current = true;
      isFetchingRef.current = true;
      hasMoreProductsRef.current = true;
      lastFetchedDocIdRef.current = null;
      // Don't reset previousProductsLengthRef here - keep old products clickable until new ones load
      // It will be reset when new products arrive in the useEffect
      let filter = SORT_BY.LATEST;
      setSavedCategory("");
      setSavedSubCategory("");
      setSavedCategorySlug("");
      setSavedSubCategorySlug("");
      setSavedSubCategoryDescription("");

      // Don't clear allProducts immediately - keep old products visible until new ones load
      setSelectedFilter(filter);
      if (category && !subCategory && category === "popular-toys") {
        setSelectedFilter(SORT_BY.POPULAR);
        filter = SORT_BY.POPULAR;
      }

      if (category && subCategory) {
        dispatch(fetchProducts(PAGE_SIZE, "", "", subCategory, filter));
      } else if (category && !subCategory) {
        dispatch(fetchProducts(PAGE_SIZE, "", category, "", filter));
      }
    }
  }, [dispatch, category, subCategory]);

  // useEffect(() => {
  //   if (products.length) {
  //     if (category && subCategory) {
  //       setSavedSubCategory(products[0].productSubCategoryName);
  //       setSavedSubCategorySlug(products[0].productSubCategorySlug);
  //       setSavedSubCategoryDescription(products[0].productDescriprion);
  //     }
  //     setSavedCategory(products[0].productCategoryName);
  //     setSavedCategorySlug(products[0].productCategorySlug);
  //     setAllProducts([...allProducts, ...products]);
  //   }
  // }, [products]);

  const filterHandler = (value: any) => {
    if (!isFetchingRef.current) {
      isFetchingRef.current = true;
      setIsReloadFromFilter(true);
      hasMoreProductsRef.current = true;
      lastFetchedDocIdRef.current = null;
      // Don't reset previousProductsLengthRef here - keep old products clickable until new ones load
      // It will be reset when new products arrive in the useEffect
      setSelectedFilter(value);
      if (category && subCategory) {
        dispatch(fetchProducts(PAGE_SIZE, "", "", subCategory, value));
      } else if (category && !subCategory) {
        dispatch(fetchProducts(PAGE_SIZE, "", category, "", value));
      }
    }
  };

  // useEffect(() => {
  //   setAllProducts([]);
  //   setSelectedFilter(SORT_BY.LATEST);
  //   if (category && !subCategory && category === "popular-toys") {
  //     setSelectedFilter(SORT_BY.POPULAR);
  //   }
  // }, [category, subCategory]);

  const handleScroll = useCallback(() => {
    const scroller = document.scrollingElement || document.documentElement;
    const { scrollTop, scrollHeight, clientHeight } = scroller;

    // Check if we're near the bottom and should load more
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 120;
    const lastDocId = allProducts[allProducts.length - 1]?.documentId;

    // Use isFetchingRef instead of loading to prevent duplicate fetches
    // This keeps the scroll listener stable (not recreated on every loading change)
    if (
      isNearBottom &&
      allProducts.length > 0 &&
      !isFetchingRef.current &&
      !isCategoryChangeRef.current && // Don't paginate during category change
      hasMoreProductsRef.current &&
      lastDocId &&
      lastDocId !== lastFetchedDocIdRef.current
    ) {
      setIScrolled(true);
      isFetchingRef.current = true;
      lastFetchedDocIdRef.current = lastDocId; // Track the cursor we're using
      if (category && subCategory) {
        dispatch(
          fetchProducts(PAGE_SIZE, lastDocId, "", subCategory, selectedFilter)
        );
      } else if (category && !subCategory) {
        dispatch(
          fetchProducts(PAGE_SIZE, lastDocId, category, "", selectedFilter)
        );
      } else {
        navigate("/");
      }
    }
  }, [allProducts, dispatch, category, subCategory, selectedFilter, navigate]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll]);

  if (!loading && (!products || products.length <= 0)) {
    if (!isScrolled) {
      navigate("/not-found");
      return null;
    }
  }

  return !isReloadFromFilter && allProducts.length === 0 ? (
    <CenteredLoading />
  ) : (
    <>
      <>
        <SEO
          title={`${
            savedSubCategory
              ? `${savedCategory} : ${savedSubCategory}`
              : savedCategory || ""
          } • FiddlePiddle • Toys Marketplace`}
          description={
            "Explore a wide range of toys on our marketplace. Buy, sell, and discover unique toys from collectors, sellers, and enthusiasts. Find your next favorite toy today!"
          }
          keywordsContent={`${
            savedSubCategory
              ? `${savedCategory}, ${savedSubCategory}`
              : savedCategory || ""
          }, toys marketplace, buy toys, sell toys, collectible toys, vintage toys, kids toys, toy deals, rare toys, toy shop, toy sales, toy collectors`}
          customJsonScript={{
            "@context": `${getOrigin()}/`,
            "@type": "CollectionPage",
            name: `${
              savedSubCategory
                ? `${savedCategory}, ${savedSubCategory}`
                : savedCategory
            } Toys`,
            description: `Browse our extensive collection of ${
              savedSubCategory
                ? `${savedCategory}, ${savedSubCategory}`
                : savedCategory
            } toys, featuring top brands and the latest trends.`,
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: `${getOrigin()}/`,
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: `${savedCategory}`,
                  item: `${getOrigin()}/${savedCategorySlug}`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: `${savedSubCategory}`,
                  item: `${getOrigin()}/${savedCategorySlug}/${savedSubCategorySlug}`,
                },
              ],
            },
          }}
        />
        <div style={{ position: "relative", zIndex: 100 }}>
          <Breadcrumb
            items={[
              { text: "Home", url: "/" },
              {
                text: savedCategory || "",
                url: `/${savedCategorySlug}`,
              },
              {
                text: savedSubCategory || "",
                url: `/${savedCategorySlug}/${savedSubCategorySlug}`,
              },
            ]}
          />
        </div>
        <Container
          maxWidth="xl"
          sx={{
            paddingTop: 0,
          }}
        >
          <div style={{ textAlign: "center", marginBottom: "1rem" }}>
            <span
              style={{
                fontSize: "1.25rem",
                fontWeight: "bold",
              }}
            >
              {savedSubCategory
                ? `${savedCategory} : ${savedSubCategory}`
                : savedCategory || ""}
            </span>
            {savedSubCategory && savedSubCategoryDescription && (
              <div style={{ marginTop: "1rem" }}>
                <p>{savedSubCategoryDescription}</p>
              </div>
            )}
          </div>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end", // Aligns children (your Box) to the right
              width: "100%", // Make sure the parent takes full width
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
                gap: 1, // Space between elements
                fontSize: "16px", // Adjust font size if needed
              }}
            >
              <span
                style={{
                  color: "#666", // Lighter color
                  fontWeight: "300", // Lighter font weight
                  fontSize: "14px", // Smaller font size
                }}
              >
                <TotalResults quantity={productsData.totalRecords} />
              </span>
              <TuneOutlinedIcon />
              <MenuList
                key={`${category}-${subCategory}-${selectedFilter}`} // Force remount when category or filter changes
                menuItems={[
                  { label: "Sort by:", value: "", isDisabled: true },
                  { label: "Latest", value: SORT_BY.LATEST },
                  { label: "Popularity", value: SORT_BY.POPULAR },
                  { label: "Lowest Price", value: SORT_BY.LOWEST_PRICE },
                  { label: "Highest Price", value: SORT_BY.HIGHEST_PRICE },
                ]}
                filterHandler={filterHandler}
                defaultValue={selectedFilter}
              />
            </Box>
          </Box>
        </Container>
      </>

      <Box bgcolor="#f0f0f0" padding="16px 0 32px">
        <Grid container spacing={2} style={{ padding: 16 }}>
          {loading && allProducts.length === 0
            ? Array.from({ length: 1 }).map((_, index) => (
                <Grid item key={index} xs={12}>
                  <Box display="flex" justifyContent="center">
                    <CircularProgress />
                  </Box>
                </Grid>
              ))
            : allProducts.map((product, index) => {
                // Only show loading state for new products being added (during pagination)
                // Existing products should not show loading state to prevent blinking
                const isNewProduct = index >= previousProductsLengthRef.current;
                return (
                  <Grid
                    item
                    key={product.documentId || index}
                    xs={6}
                    sm={4}
                    md={3}
                    lg={2}
                  >
                    <ToyCard
                      product={product}
                      loading={false && isNewProduct}
                    />
                  </Grid>
                );
              })}
          {loading &&
            allProducts.length >= PAGE_SIZE &&
            hasMoreProductsRef.current && (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" marginTop={2}>
                  <CircularProgress />
                </Box>
              </Grid>
            )}
        </Grid>
      </Box>
    </>
  );
};

export default ProductByCategory;
